/**
 * OpenAI Ads Measurement Pixel — event helpers.
 *
 * The pixel SDK is loaded by `public/oaiq-loader.js`, which is appended to
 * <head> at runtime by `src/components/analytics/OpenAiPixelLoader.tsx` only
 * when the Unleash flag `chatgpt-ads-pixel` is ON, and which only calls
 * `init` on the production hostname. Everything in this module therefore has
 * to survive `window.oaiq` being undefined — that is the NORMAL state on dev,
 * with the flag off, and for any visitor running an ad blocker.
 *
 * Design rules this module enforces:
 *
 *   - `measure()` NEVER throws and never assumes the pixel exists. Every call
 *     site can fire unconditionally.
 *   - GPC is honored here as well as in the loader. The loader calls
 *     `oaiq("consent", false)` before init, and this wrapper additionally
 *     refuses to emit. Belt and braces: a future refactor that loads the SDK
 *     some other way must not silently start sending events for a GPC session.
 *   - Dedup ids are DETERMINISTIC and must match what the Conversions API
 *     sends for the same conversion. Dedup key is pixel id + event name + id,
 *     first received wins. Never a client-generated UUID.
 *     See docs/marketing/paid-ads/chatgpt-ads.md § Identifiers, storage, dedup.
 *
 * Money events (`subscription_created`, `order_created`) are deliberately NOT
 * here — they are CAPI-only, fired from Stripe webhooks in the payment
 * service, because the browser cannot be trusted for revenue values and an ad
 * blocker must not be able to drop a conversion.
 */

/** Events this app is allowed to fire from the browser. */
export type OpenAiAdsEvent =
  | 'page_viewed'
  | 'contents_viewed'
  | 'checkout_started'
  | 'registration_completed';

type OaiqFn = ((command: string, ...args: unknown[]) => void) & { q?: unknown[] };

/**
 * Returns the pixel function only if the SDK stub is actually present.
 * Undefined is the expected case off-prod and with the flag off.
 */
function getOaiq(): OaiqFn | null {
  if (typeof window === 'undefined') return null;
  const candidate = (window as unknown as { oaiq?: unknown }).oaiq;
  return typeof candidate === 'function' ? (candidate as OaiqFn) : null;
}

/**
 * Global Privacy Control. `navigator.globalPrivacyControl` is a non-standard
 * (but widely shipped) boolean; treat anything other than exactly `true` as
 * "not set" so a string "1" or an undefined never accidentally opts a user out
 * or in by coercion.
 */
export function isGpcEnabled(): boolean {
  if (typeof navigator === 'undefined') return false;
  return (
    (navigator as Navigator & { globalPrivacyControl?: boolean }).globalPrivacyControl === true
  );
}

/**
 * Fire a pixel event. Returns whether the event was actually handed to the SDK
 * — false means "no pixel" or "GPC", both of which are normal, neither of
 * which is an error.
 *
 * @param event    OpenAI event name
 * @param data     Event payload (`type` is required by OpenAI for every event)
 * @param eventId  Deterministic dedup id, shared with the CAPI side. Omit for
 *                 pixel-only events (`page_viewed`, `contents_viewed`,
 *                 `checkout_started`) which have no server counterpart.
 */
export function measure(
  event: OpenAiAdsEvent,
  data: Record<string, unknown> = {},
  eventId?: string
): boolean {
  if (isGpcEnabled()) return false;

  const oaiq = getOaiq();
  if (!oaiq) return false;

  try {
    const payload: Record<string, unknown> = { ...data };
    if (eventId) payload.event_id = eventId;
    oaiq('measure', event, payload);
    return true;
  } catch {
    // A pixel failure must never surface to the user or break a checkout.
    return false;
  }
}

/** Route change / ad landing. Funnel visibility only — carries no bid signal. */
export function trackPageViewed(): boolean {
  return measure('page_viewed', { type: 'contents' });
}

/** Sales-page view (`/` and `/nextgen-prep`). */
export function trackContentsViewed(): boolean {
  return measure('contents_viewed', { type: 'contents' });
}

/**
 * Checkout opened. Pixel-only — there is no authoritative server hook for it
 * (creating a PaymentIntent is not a started checkout).
 *
 * @param amountMinorUnits Amount in MINOR units (2000 = $20.00). OpenAI
 *                         requires `currency` whenever `amount` is present.
 */
export function trackCheckoutStarted(
  amountMinorUnits: number,
  planId?: string
): boolean {
  const data: Record<string, unknown> = {
    type: 'plan_enrollment',
    amount: Math.max(0, Math.round(amountMinorUnits)),
    currency: 'USD',
  };
  if (planId) data.plan_id = planId;
  return measure('checkout_started', data);
}

/**
 * Account created. Dual-fired with CAPI (`routes/signup.ts`), so the id must
 * be byte-identical on both sides: `reg_<firebase uid>`. The Firebase uid is
 * the JDS user_id and profiles.id, so the browser can derive it.
 */
export function trackRegistrationCompleted(firebaseUid: string): boolean {
  if (!firebaseUid) return false;
  return measure('registration_completed', { type: 'customer_action' }, `reg_${firebaseUid}`);
}

/**
 * NOTE — `trial_started` is intentionally absent.
 *
 * The shared dedup id is `trial_sub_<stripe subscription id>`, and the browser
 * does not reliably hold that id at trial-start time. Rather than invent a
 * second id scheme (which would defeat dedup and double-count every trial),
 * the browser skips the event entirely and CAPI owns it. Add a helper here
 * ONLY if a checkout-confirmation response starts returning the Stripe
 * subscription id. See chatgpt-ads.md § Identifiers, storage, dedup.
 */
